import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Cookie } from 'lucide-react';

const STORAGE_KEY = 'ryze-cookie-consent';

type ConsentState = 'accepted' | 'declined' | null;

export function CookieBanner() {
  const { t } = useTranslation();
  const [consent, setConsent] = useState<ConsentState>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [visible, setVisible] = useState(false);
  const isMarketingSurface = typeof window !== 'undefined' && [
    '/',
    '/pricing',
    '/about',
    '/customers',
    '/careers',
    '/blog',
    '/press',
  ].some(path => window.location.pathname === path || window.location.pathname.startsWith(`${path}/`));

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ConsentState;
    if (stored) {
      setConsent(stored);
    } else {
      // Show after a short delay so it doesn't flash immediately on load
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setConsent('accepted');
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(STORAGE_KEY, 'declined');
    setConsent('declined');
    setVisible(false);
  };

  // Suppress the banner on render routes — headless PDF snapshots of
  // /r/:reportId shouldn't include a cookie consent prompt in the exported PDF.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/r/')) {
    return null;
  }

  if (!visible || consent !== null) return null;

  const cookieCategories = [
    {
      name: t('cookieBanner.essential'),
      desc: t('cookieBanner.essentialDescription'),
      required: true,
    },
    {
      name: t('cookieBanner.analytics'),
      desc: t('cookieBanner.analyticsDescription'),
      required: false,
    },
  ];

  return (
    <aside
      className={`ryze-cookie-banner${isMarketingSurface ? ' ryze-cookie-banner--marketing' : ''}`}
      role="dialog"
      aria-live="polite"
      aria-labelledby="ryze-cookie-banner-title"
      aria-describedby="ryze-cookie-banner-description"
    >
      <div className="ryze-cookie-banner__glow" aria-hidden="true" />

      <div className="ryze-cookie-banner__header">
        <div className="ryze-cookie-banner__icon" aria-hidden="true">
          <Cookie className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="ryze-cookie-banner__eyebrow">Ryze Analytics</p>
          <h2 id="ryze-cookie-banner-title" className="ryze-cookie-banner__title">
            {t('cookieBanner.title')}
          </h2>
        </div>

        <button
          type="button"
          onClick={handleDecline}
          aria-label={t('cookieBanner.close')}
          className="ryze-cookie-banner__close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <p id="ryze-cookie-banner-description" className="ryze-cookie-banner__description">
        {t('cookieBanner.description')}
      </p>

      <button
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        className="ryze-cookie-banner__learn-more"
        aria-expanded={showDetails}
        aria-controls="ryze-cookie-details"
      >
        {showDetails ? t('cookieBanner.hideDetails') : t('cookieBanner.learnMore')}
      </button>

      {showDetails && (
        <div id="ryze-cookie-details" className="ryze-cookie-banner__details">
          {cookieCategories.map(c => (
            <div key={c.name} className="ryze-cookie-banner__category">
              <div
                className={`ryze-cookie-banner__check${c.required ? ' is-required' : ''}`}
                aria-hidden="true"
              >
                {c.required && <span>✓</span>}
              </div>
              <div>
                <div className="ryze-cookie-banner__category-title">
                  <span>{c.name}</span>
                  {c.required && <small>{t('cookieBanner.required')}</small>}
                </div>
                <p>{c.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="ryze-cookie-banner__actions">
        <button
          type="button"
          onClick={handleDecline}
          className="ryze-cookie-banner__button ryze-cookie-banner__button--secondary"
        >
          {t('cookieBanner.essentialOnly')}
        </button>
        <button
          type="button"
          onClick={handleAccept}
          className="ryze-cookie-banner__button ryze-cookie-banner__button--primary"
        >
          {t('cookieBanner.acceptAll')}
        </button>
      </div>
    </aside>
  );
}
